import { Clipboard } from '@angular/cdk/clipboard';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgCommonModule } from '@principle-theorem/ng-shared';
import {
  DepositStatus,
  type IDepositMetadata,
} from '@principle-theorem/principle-core/interfaces';
import { HISTORY_DATE_FORMAT } from '@principle-theorem/shared';
import { DepositActionsComponent } from '../../scheduling/deposit-actions/deposit-actions.component';
import {
  DEPOSIT_CAPTURE_STRATEGY_FALLBACK_DESCRIPTIONS,
  DEPOSIT_CAPTURE_STRATEGY_FALLBACK_LABELS,
  DEPOSIT_CAPTURE_STRATEGY_LABELS,
  DEPOSIT_CAPTURE_STRATEGY_TOOLTIPS,
  DEPOSIT_PAYMENT_MODE_LABELS,
} from './deposit-capture-strategy-display';
import {
  DEPOSIT_SOURCE_LABELS,
  DEPOSIT_SOURCE_TOOLTIPS,
} from './deposit-source-display';
import { IWaiveDepositDialogResult } from './waive-deposit-dialog/waive-deposit-dialog.component';

/**
 * The deposit panel of the inspect-appointment sidebar.
 *
 * The status and the actions come from the shared deposit row, so they match
 * the requests table and the awaiting-deposit list. This panel only adds the
 * detail a saved appointment can carry: how the money was taken, when it was
 * paid, the attempt behind it, and who waived it.
 */
@Component({
  selector: 'pr-appointment-deposit-sidebar',
  templateUrl: './appointment-deposit-sidebar.component.html',
  styleUrls: ['./appointment-deposit-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    DepositActionsComponent,
  ],
})
export class AppointmentDepositSidebarComponent {
  private _clipboard = inject(Clipboard);
  private _snackBar = inject(MatSnackBar);

  readonly deposit = input<IDepositMetadata>();
  /** Must be derived from the `appointment.deposit.override` permission. */
  readonly canOverrideDeposit = input(false);
  readonly waiveDeposit = output<IWaiveDepositDialogResult>();
  readonly unwaiveDeposit = output();
  readonly takeDeposit = output();

  readonly dateFormat = HISTORY_DATE_FORMAT;
  readonly depositCaptureStrategyFallbackDescriptions =
    DEPOSIT_CAPTURE_STRATEGY_FALLBACK_DESCRIPTIONS;
  readonly depositCaptureStrategyFallbackLabels =
    DEPOSIT_CAPTURE_STRATEGY_FALLBACK_LABELS;
  readonly depositCaptureStrategyTooltips = DEPOSIT_CAPTURE_STRATEGY_TOOLTIPS;
  readonly depositSourceLabels = DEPOSIT_SOURCE_LABELS;
  readonly depositSourceTooltips = DEPOSIT_SOURCE_TOOLTIPS;
  DepositStatus = DepositStatus;

  copyPaymentAttemptId(paymentAttemptId: string): void {
    this._clipboard.copy(paymentAttemptId);
    this._snackBar.open('Payment attempt ID copied');
  }

  getPaymentModeLabel(deposit: IDepositMetadata): string {
    if (
      deposit.status === DepositStatus.Paid &&
      deposit.paymentAttemptRef === undefined
    ) {
      return 'Recorded at practice';
    }

    if (deposit.captureStrategy) {
      return DEPOSIT_CAPTURE_STRATEGY_LABELS[deposit.captureStrategy];
    }

    return DEPOSIT_PAYMENT_MODE_LABELS[deposit.paymentMode];
  }
}
